import React, { useState } from 'react';
import {
  LuCheck,
  LuChevronLeft,
  LuChevronRight,
  LuMail,
  LuPencil,
  LuPlus,
  LuTrash2,
  LuUserPlus,
  LuX,
} from 'react-icons/lu';

import {
  StandaloneLabel,
  StandaloneProject,
  StandaloneProjectMember,
  StandaloneStatus,
} from '../../entities/standalone-project';
import {
  createStandaloneLabel,
  createStandaloneStatus,
  deleteStandaloneLabel,
  deleteStandaloneStatus,
  inviteStandaloneMember,
  removeStandaloneMember,
  reorderStandaloneStatuses,
  standaloneErrorMessage,
  updateStandaloneLabel,
  updateStandaloneStatus,
} from '../../shared/api/standalone';
import { auth } from '../../shared/firebase/firebase';
import { invalidateStandaloneProjectsCache } from '../../shared/hooks/use-standalone-projects';
import { Modal } from '../ui/Modal';

interface BoardSettingsModalProps {
  project: StandaloneProject;
  onClose: () => void;
  onUpdated: (project: StandaloneProject) => void;
}

const CATEGORY_LABEL: Record<StandaloneStatus['category'], string> = {
  todo: 'To do',
  in_progress: 'In progress',
  done: 'Done',
};

// MF-39 — only an in_progress-category status can hold a coding-agent role (server-enforced).
const AGENT_ROLE_LABEL: Record<'working' | 'review', string> = {
  working: 'Working',
  review: 'Review',
};

const AGENT_ROLE_HINT: Record<'working' | 'review', string> = {
  working: 'The coding agent moves a task here when it starts running.',
  review: "Where it moves the task when it finishes (failure/cancel returns it to the task's previous status).",
};

type AgentRoleChoice = 'none' | 'working' | 'review';

type Tab = 'columns' | 'labels' | 'members';

const MEMBER_ROLE_LABEL: Record<StandaloneProjectMember['role'], string> = {
  owner: 'Owner',
  member: 'Member',
};

const BoardSettingsModal: React.FC<BoardSettingsModalProps> = ({ project, onClose, onUpdated }) => {
  const [tab, setTab] = useState<Tab>('columns');
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [newStatusName, setNewStatusName] = useState('');
  const [newStatusCategory, setNewStatusCategory] = useState<StandaloneStatus['category']>('todo');
  const [isAddingStatus, setIsAddingStatus] = useState(false);

  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState('#6b7280');
  const [isAddingLabel, setIsAddingLabel] = useState(false);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<StandaloneProjectMember['role']>('member');
  const [isInviting, setIsInviting] = useState(false);

  // Inline rename, shared shape for both statuses and labels (only one row edits at a time).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingColor, setEditingColor] = useState('');
  const [editingAgentRole, setEditingAgentRole] = useState<AgentRoleChoice>('none');

  // Delete-blocked reassign flow (statuses only — see standalone-contract.md §0).
  const [reassignFor, setReassignFor] = useState<string | null>(null);
  const [reassignTo, setReassignTo] = useState('');

  const statuses = [...project.statuses].sort((a, b) => a.order - b.order);

  const startEdit = (id: string, name: string, color?: string, agentRole?: 'working' | 'review') => {
    setEditingId(id);
    setEditingName(name);
    setEditingColor(color ?? '#6b7280');
    setEditingAgentRole(agentRole ?? 'none');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const saveStatusEdit = async (status: StandaloneStatus) => {
    if (!editingName.trim()) return;
    setBusyId(status.id);
    setError(null);
    try {
      const updated = await updateStandaloneStatus(project._id, status.id, {
        name: editingName.trim(),
        color: editingColor,
        agentRole: editingAgentRole === 'none' ? null : editingAgentRole,
      });
      onUpdated(updated);
      cancelEdit();
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to update column.');
    } finally {
      setBusyId(null);
    }
  };

  const saveLabelEdit = async (label: StandaloneLabel) => {
    if (!editingName.trim()) return;
    setBusyId(label.id);
    setError(null);
    try {
      const updated = await updateStandaloneLabel(project._id, label.id, {
        name: editingName.trim(),
        color: editingColor,
      });
      onUpdated(updated);
      cancelEdit();
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to update label.');
    } finally {
      setBusyId(null);
    }
  };

  const handleAddStatus = async () => {
    if (!newStatusName.trim()) return;
    setIsAddingStatus(true);
    setError(null);
    try {
      const updated = await createStandaloneStatus(project._id, {
        name: newStatusName.trim(),
        category: newStatusCategory,
      });
      onUpdated(updated);
      setNewStatusName('');
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to add column.');
    } finally {
      setIsAddingStatus(false);
    }
  };

  const handleAddLabel = async () => {
    if (!newLabelName.trim()) return;
    setIsAddingLabel(true);
    setError(null);
    try {
      const updated = await createStandaloneLabel(project._id, { name: newLabelName.trim(), color: newLabelColor });
      onUpdated(updated);
      setNewLabelName('');
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to add label.');
    } finally {
      setIsAddingLabel(false);
    }
  };

  const handleMove = async (statusId: string, direction: -1 | 1) => {
    const idx = statuses.findIndex((s) => s.id === statusId);
    const swapWith = idx + direction;
    if (idx < 0 || swapWith < 0 || swapWith >= statuses.length) return;
    const next = [...statuses];
    [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
    setBusyId(statusId);
    setError(null);
    try {
      const updated = await reorderStandaloneStatuses(
        project._id,
        next.map((s) => s.id),
      );
      onUpdated(updated);
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to reorder columns.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteStatus = async (statusId: string) => {
    setBusyId(statusId);
    setError(null);
    try {
      const updated = await deleteStandaloneStatus(project._id, statusId);
      onUpdated(updated);
    } catch (err) {
      const msg = standaloneErrorMessage(err);
      if (msg?.includes('reassignToStatusId')) {
        setReassignFor(statusId);
        setReassignTo(statuses.find((s) => s.id !== statusId)?.id ?? '');
      } else {
        setError(msg ?? 'Failed to delete column.');
      }
    } finally {
      setBusyId(null);
    }
  };

  const confirmReassignDelete = async () => {
    if (!reassignFor || !reassignTo) return;
    setBusyId(reassignFor);
    setError(null);
    try {
      const updated = await deleteStandaloneStatus(project._id, reassignFor, reassignTo);
      onUpdated(updated);
      setReassignFor(null);
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to delete column.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteLabel = async (labelId: string) => {
    if (!window.confirm('Delete this label? It will be removed from every task that has it.')) return;
    setBusyId(labelId);
    setError(null);
    try {
      const updated = await deleteStandaloneLabel(project._id, labelId);
      onUpdated(updated);
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to delete label.');
    } finally {
      setBusyId(null);
    }
  };

  // MF-58 — a project that predates sharing has no `members[]` at all; its creator is still its
  // de facto sole owner either way.
  const members = project.members ?? [];
  const myUserId = auth.currentUser?.uid;
  const isOwner =
    members.length === 0 || members.some((m) => m.userId === myUserId && m.role === 'owner' && m.status === 'accepted');
  const acceptedOwnerCount = members.filter((m) => m.role === 'owner' && m.status === 'accepted').length;

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setIsInviting(true);
    setError(null);
    try {
      const updated = await inviteStandaloneMember(project._id, inviteEmail.trim(), inviteRole);
      onUpdated(updated);
      setInviteEmail('');
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to send invite.');
    } finally {
      setIsInviting(false);
    }
  };

  // Leave must target by userId specifically — targeting by email is treated as "remove someone
  // else", not self-removal (confirmed with the BE session).
  const handleLeave = async () => {
    if (!myUserId) return;
    if (!window.confirm('Leave this project? You will lose access to it.')) return;
    setBusyId(myUserId);
    setError(null);
    try {
      const updated = await removeStandaloneMember(project._id, { userId: myUserId });
      onUpdated(updated);
      await invalidateStandaloneProjectsCache();
      onClose();
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to leave the project.');
    } finally {
      setBusyId(null);
    }
  };

  const handleRemoveMember = async (member: StandaloneProjectMember) => {
    const target = member.userId ? { userId: member.userId } : { email: member.email };
    const busyKey = member.userId ?? member.email ?? '';
    if (!window.confirm(`Remove ${member.email ?? 'this member'} from the project?`)) return;
    setBusyId(busyKey);
    setError(null);
    try {
      const updated = await removeStandaloneMember(project._id, target);
      onUpdated(updated);
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to remove member.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Modal onClose={onClose} className="max-w-lg" labelledBy="board-settings-title">
      <div className="card-body p-5 gap-3">
        <div className="flex items-center justify-between">
          <div id="board-settings-title" className="text-lg font-semibold">
            Board settings
          </div>
          <button className="btn btn-ghost btn-sm btn-circle" onClick={onClose}>
            <LuX size={16} />
          </button>
        </div>

        <div role="tablist" className="tabs tabs-boxed w-fit">
          <button
            role="tab"
            className={`tab ${tab === 'columns' ? 'tab-active' : ''}`}
            onClick={() => setTab('columns')}
          >
            Columns
          </button>
          <button role="tab" className={`tab ${tab === 'labels' ? 'tab-active' : ''}`} onClick={() => setTab('labels')}>
            Labels
          </button>
          <button
            role="tab"
            className={`tab ${tab === 'members' ? 'tab-active' : ''}`}
            onClick={() => setTab('members')}
          >
            Members
          </button>
        </div>

        {error && (
          <div className="alert alert-error py-2">
            <span className="text-sm">{error}</span>
          </div>
        )}

        {tab === 'columns' && (
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            <p className="text-xs opacity-50">
              Give an &quot;In progress&quot; column a coding-agent role to have the agent move tasks automatically:{' '}
              <span className="font-medium">Working</span> — where it puts a task once it starts running;{' '}
              <span className="font-medium">Review</span> — where it puts the task when it finishes.
            </p>
            <div className="space-y-1.5">
              {statuses.map((status, idx) => (
                <div key={status.id} className="rounded-lg bg-base-200 px-3 py-2 space-y-2">
                  {editingId === status.id ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          className="w-7 h-7 rounded cursor-pointer border-0 bg-transparent shrink-0"
                          value={editingColor}
                          onChange={(e) => setEditingColor(e.target.value)}
                        />
                        <input
                          className="input input-bordered input-sm flex-1"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && saveStatusEdit(status)}
                        />
                        <button className="btn btn-ghost btn-xs btn-circle" onClick={() => saveStatusEdit(status)}>
                          <LuCheck size={14} />
                        </button>
                        <button className="btn btn-ghost btn-xs btn-circle" onClick={cancelEdit}>
                          <LuX size={14} />
                        </button>
                      </div>
                      {status.category === 'in_progress' ? (
                        <div className="flex items-center gap-2 pl-9">
                          <span className="text-xs opacity-60 shrink-0">Coding agent role</span>
                          <select
                            className="select select-bordered select-xs"
                            value={editingAgentRole}
                            onChange={(e) => setEditingAgentRole(e.target.value as AgentRoleChoice)}
                          >
                            <option value="none">None</option>
                            <option value="working">Working</option>
                            <option value="review">Review</option>
                          </select>
                          {editingAgentRole !== 'none' && (
                            <span className="text-xs opacity-50">{AGENT_ROLE_HINT[editingAgentRole]}</span>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs opacity-40 pl-9">
                          Only an &quot;In progress&quot; column can hold a coding-agent role.
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span
                        className="w-3 h-3 rounded-full shrink-0"
                        style={{ backgroundColor: status.color || '#9ca3af' }}
                      />
                      <span className="text-sm font-medium flex-1 truncate">{status.name}</span>
                      <span className="badge badge-ghost badge-xs">{CATEGORY_LABEL[status.category]}</span>
                      {status.agentRole && (
                        <span className="badge badge-primary badge-xs" title={AGENT_ROLE_HINT[status.agentRole]}>
                          {AGENT_ROLE_LABEL[status.agentRole]}
                        </span>
                      )}
                      <button
                        className="btn btn-ghost btn-xs btn-circle"
                        onClick={() => handleMove(status.id, -1)}
                        disabled={idx === 0 || busyId === status.id}
                        title="Move left"
                      >
                        <LuChevronLeft size={13} />
                      </button>
                      <button
                        className="btn btn-ghost btn-xs btn-circle"
                        onClick={() => handleMove(status.id, 1)}
                        disabled={idx === statuses.length - 1 || busyId === status.id}
                        title="Move right"
                      >
                        <LuChevronRight size={13} />
                      </button>
                      <button
                        className="btn btn-ghost btn-xs btn-circle"
                        onClick={() => startEdit(status.id, status.name, status.color, status.agentRole)}
                        title="Rename"
                      >
                        <LuPencil size={13} />
                      </button>
                      <button
                        className="btn btn-ghost btn-xs btn-circle text-error"
                        onClick={() => handleDeleteStatus(status.id)}
                        disabled={busyId === status.id || statuses.length === 1}
                        title={statuses.length === 1 ? "Can't delete the only column" : 'Delete'}
                      >
                        {busyId === status.id ? (
                          <span className="loading loading-spinner loading-xs" />
                        ) : (
                          <LuTrash2 size={13} />
                        )}
                      </button>
                    </div>
                  )}

                  {reassignFor === status.id && (
                    <div className="flex items-center gap-2 pl-5 text-xs flex-wrap">
                      <span className="opacity-70">Move its tasks to:</span>
                      <select
                        className="select select-bordered select-xs"
                        value={reassignTo}
                        onChange={(e) => setReassignTo(e.target.value)}
                      >
                        {statuses
                          .filter((s) => s.id !== status.id)
                          .map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                      </select>
                      <button className="btn btn-error btn-xs" onClick={confirmReassignDelete}>
                        Delete
                      </button>
                      <button className="btn btn-ghost btn-xs" onClick={() => setReassignFor(null)}>
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 pt-3 border-t border-base-300">
              <input
                className="input input-bordered input-sm flex-1"
                placeholder="New column name"
                value={newStatusName}
                onChange={(e) => setNewStatusName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddStatus()}
              />
              <select
                className="select select-bordered select-sm"
                value={newStatusCategory}
                onChange={(e) => setNewStatusCategory(e.target.value as StandaloneStatus['category'])}
              >
                <option value="todo">To do</option>
                <option value="in_progress">In progress</option>
                <option value="done">Done</option>
              </select>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleAddStatus}
                disabled={!newStatusName.trim() || isAddingStatus}
              >
                {isAddingStatus ? <span className="loading loading-spinner loading-xs" /> : <LuPlus size={14} />}
              </button>
            </div>
          </div>
        )}

        {tab === 'labels' && (
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            <div className="space-y-1.5">
              {project.labels.length === 0 && <div className="text-xs opacity-50 py-2">No labels yet.</div>}
              {project.labels.map((label) => (
                <div key={label.id} className="rounded-lg bg-base-200 px-3 py-2 flex items-center gap-2">
                  {editingId === label.id ? (
                    <>
                      <input
                        type="color"
                        className="w-7 h-7 rounded cursor-pointer border-0 bg-transparent shrink-0"
                        value={editingColor}
                        onChange={(e) => setEditingColor(e.target.value)}
                      />
                      <input
                        className="input input-bordered input-sm flex-1"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && saveLabelEdit(label)}
                      />
                      <button className="btn btn-ghost btn-xs btn-circle" onClick={() => saveLabelEdit(label)}>
                        <LuCheck size={14} />
                      </button>
                      <button className="btn btn-ghost btn-xs btn-circle" onClick={cancelEdit}>
                        <LuX size={14} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span
                        className="w-3 h-3 rounded-full shrink-0"
                        style={{ backgroundColor: label.color || '#9ca3af' }}
                      />
                      <span className="text-sm font-medium flex-1 truncate">{label.name}</span>
                      <button
                        className="btn btn-ghost btn-xs btn-circle"
                        onClick={() => startEdit(label.id, label.name, label.color)}
                        title="Rename"
                      >
                        <LuPencil size={13} />
                      </button>
                      <button
                        className="btn btn-ghost btn-xs btn-circle text-error"
                        onClick={() => handleDeleteLabel(label.id)}
                        disabled={busyId === label.id}
                        title="Delete"
                      >
                        {busyId === label.id ? (
                          <span className="loading loading-spinner loading-xs" />
                        ) : (
                          <LuTrash2 size={13} />
                        )}
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 pt-3 border-t border-base-300">
              <input
                type="color"
                className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent shrink-0"
                value={newLabelColor}
                onChange={(e) => setNewLabelColor(e.target.value)}
              />
              <input
                className="input input-bordered input-sm flex-1"
                placeholder="New label name"
                value={newLabelName}
                onChange={(e) => setNewLabelName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddLabel()}
              />
              <button
                className="btn btn-primary btn-sm"
                onClick={handleAddLabel}
                disabled={!newLabelName.trim() || isAddingLabel}
              >
                {isAddingLabel ? <span className="loading loading-spinner loading-xs" /> : <LuPlus size={14} />}
              </button>
            </div>
          </div>
        )}

        {tab === 'members' && (
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            <div className="space-y-1.5">
              {members.length === 0 ? (
                <div className="text-xs opacity-50 py-2">Just you — no one else has access to this project yet.</div>
              ) : (
                members.map((member) => {
                  const key = member.userId ?? member.email ?? `${member.role}-${member.invitedAt}`;
                  const isSelf = !!myUserId && member.userId === myUserId;
                  const isBusy = busyId === (member.userId ?? member.email);
                  const isLastOwner =
                    member.role === 'owner' && member.status === 'accepted' && acceptedOwnerCount <= 1;
                  return (
                    <div key={key} className="rounded-lg bg-base-200 px-3 py-2 flex items-center gap-2">
                      <LuMail className="opacity-40 shrink-0" size={14} />
                      <div className="min-w-0 flex-1">
                        <span className="text-sm font-medium truncate block">
                          {member.email ?? member.userId ?? 'Unknown'}
                          {isSelf && <span className="opacity-50"> (you)</span>}
                        </span>
                      </div>
                      <span className="badge badge-ghost badge-xs shrink-0">{MEMBER_ROLE_LABEL[member.role]}</span>
                      {member.status === 'pending' && (
                        <span className="badge badge-warning badge-xs shrink-0">Invited — waiting to accept</span>
                      )}
                      {isSelf ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs btn-circle text-error shrink-0"
                          onClick={handleLeave}
                          disabled={isBusy || isLastOwner}
                          title={isLastOwner ? "Can't leave — you're the only owner" : 'Leave project'}
                        >
                          {isBusy ? <span className="loading loading-spinner loading-xs" /> : <LuX size={13} />}
                        </button>
                      ) : (
                        isOwner && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-xs btn-circle text-error shrink-0"
                            onClick={() => handleRemoveMember(member)}
                            disabled={isBusy || isLastOwner}
                            title={isLastOwner ? "Can't remove the only owner" : 'Remove from project'}
                          >
                            {isBusy ? <span className="loading loading-spinner loading-xs" /> : <LuTrash2 size={13} />}
                          </button>
                        )
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {isOwner ? (
              <div className="flex items-center gap-2 pt-3 border-t border-base-300">
                <input
                  type="email"
                  className="input input-bordered input-sm flex-1"
                  placeholder="Invite by email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                />
                <select
                  className="select select-bordered select-sm"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as StandaloneProjectMember['role'])}
                >
                  <option value="member">Member</option>
                  <option value="owner">Owner</option>
                </select>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleInvite}
                  disabled={!inviteEmail.trim() || isInviting}
                >
                  {isInviting ? <span className="loading loading-spinner loading-xs" /> : <LuUserPlus size={14} />}
                </button>
              </div>
            ) : (
              <p className="text-xs opacity-40 pt-3 border-t border-base-300">Only an owner can invite new members.</p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BoardSettingsModal;
